import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Plus, Pencil, Trash2, X, Upload, Image, Star, Tag, CheckCircle, Video, Play, Clock, CalendarClock, Send } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import DraggableModal from '../../components/ui/DraggableModal';
import { adminFetch } from '../../utils/adminFetch';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminBlog = () => {
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadingGallery, setUploadingGallery] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0, completed: [] });
  const [categoryName, setCategoryName] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [form, setForm] = useState({
    title: '', category: '', content: '', excerpt: '',
    date: '', location: '', thumbnail: '', images: [], videos: [],
    tags: [],
    status: 'published',
    publish_date: '',
    coordinates: { lat: '', lng: '' }
  });

  useEffect(() => { 
    fetchPosts(); 
    fetchCategories();
  }, []);

  const fetchPosts = async () => {
    try {
      const response = await adminFetch(`${API}/blog?include_all=true`);
      setPosts(await response.json());
    } catch (error) {
      toast.error('Error al cargar posts');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await adminFetch(`${API}/admin/blog-categories`);
      if (response.ok) {
        const data = await response.json();
        setCategories(data);
        // Set default category if form is empty
        if (data.length > 0 && !form.category) {
          setForm(prev => ({ ...prev, category: data[0].name }));
        }
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('section', 'blog');
    if (form.title) {
      formData.append('subpath', form.title);
      formData.append('filename_hint', `${form.title}-portada`);
    }
    try {
      const response = await adminFetch(`${API}/admin/upload`, {
        method: 'POST', body: formData
      });
      const text = await response.text();
      if (response.ok) {
        const data = JSON.parse(text);
        setForm(prev => ({ ...prev, thumbnail: data.url }));
        toast.success('Imagen subida');
      } else {
        throw new Error('Upload failed');
      }
    } catch (error) {
      toast.error('Error al subir imagen');
    } finally {
      setUploading(false);
    }
  };

  const handleGalleryUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    
    setUploadingGallery(true);
    setUploadProgress({ current: 0, total: files.length, completed: [] });
    const uploadedUrls = [];
    
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const formData = new FormData();
      formData.append('file', file);
      formData.append('section', 'blog');
      if (form.title) {
        formData.append('subpath', form.title);
        formData.append('filename_hint', `${form.title}-${String(i + 1).padStart(3, '0')}`);
      }
      try {
        const response = await adminFetch(`${API}/admin/upload`, {
          method: 'POST', body: formData
        });
        const text = await response.text();
        if (response.ok) {
          const data = JSON.parse(text);
          uploadedUrls.push(data.url);
          setUploadProgress(prev => ({
            current: prev.current + 1,
            total: prev.total,
            completed: [...prev.completed, file.name]
          }));
        }
      } catch (error) {
        console.error('Error uploading:', error);
        setUploadProgress(prev => ({
          ...prev,
          current: prev.current + 1
        }));
      }
    }
    
    if (uploadedUrls.length > 0) {
      setForm(prev => ({ ...prev, images: [...prev.images, ...uploadedUrls] }));
      toast.success(`${uploadedUrls.length} imagen(es) añadida(s) a la galería`);
    }
    setUploadingGallery(false);
    setUploadProgress({ current: 0, total: 0, completed: [] });
  };

  const removeGalleryImage = (index) => {
    setForm(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  // Video management
  const [newVideoUrl, setNewVideoUrl] = useState('');
  const [newVideoTitle, setNewVideoTitle] = useState('');

  const detectVideoType = (url) => {
    if (url.includes('youtube.com') || url.includes('youtu.be')) {
      return 'youtube';
    } else if (url.includes('cloudinary.com')) {
      return 'cloudinary';
    }
    return 'unknown';
  };

  const getYouTubeId = (url) => {
    const match = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/);
    return match ? match[1] : null;
  };

  const addVideo = () => {
    if (!newVideoUrl.trim()) {
      toast.error('Introduce una URL de video');
      return;
    }
    
    const videoType = detectVideoType(newVideoUrl);
    if (videoType === 'unknown') {
      toast.error('URL no válida. Solo se permiten videos de YouTube o Cloudinary');
      return;
    }

    if (videoType === 'youtube' && !getYouTubeId(newVideoUrl)) {
      toast.error('URL de YouTube no válida');
      return;
    }

    const newVideo = {
      video_id: `vid_${Date.now()}`,
      url: newVideoUrl.trim(),
      title: newVideoTitle.trim() || 'Video sin título',
      video_type: videoType
    };

    setForm(prev => ({
      ...prev,
      videos: [...prev.videos, newVideo]
    }));

    setNewVideoUrl('');
    setNewVideoTitle('');
    toast.success('Video añadido');
  };

  const removeVideo = (videoId) => {
    setForm(prev => ({
      ...prev,
      videos: prev.videos.filter(v => v.video_id !== videoId)
    }));
  };

  const getVideoThumbnail = (video) => {
    if (video.video_type === 'youtube') {
      const youtubeId = getYouTubeId(video.url);
      return youtubeId ? `https://img.youtube.com/vi/${youtubeId}/mqdefault.jpg` : null;
    }
    return null;
  };

  // Video upload to Cloudinary
  const [uploadingVideo, setUploadingVideo] = useState(false);

  const handleVideoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check file size (100MB limit for Cloudinary free plan)
    const maxSize = 100 * 1024 * 1024; // 100MB in bytes
    if (file.size > maxSize) {
      toast.error('El video es demasiado grande. Máximo 100MB');
      return;
    }

    // Check file type
    const validTypes = ['video/mp4', 'video/mov', 'video/avi', 'video/webm', 'video/quicktime'];
    if (!validTypes.includes(file.type) && !file.name.match(/\.(mp4|mov|avi|webm|mkv|m4v)$/i)) {
      toast.error('Formato de video no válido. Usa MP4, MOV, AVI o WEBM');
      return;
    }

    setUploadingVideo(true);
    toast.info('Subiendo video... Esto puede tardar unos segundos');

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await adminFetch(`${API}/admin/upload`, {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        
        // Create video entry
        const newVideo = {
          video_id: `vid_${Date.now()}`,
          url: data.url,
          title: file.name.replace(/\.[^/.]+$/, ''), // Remove extension for title
          video_type: 'cloudinary'
        };

        setForm(prev => ({
          ...prev,
          videos: [...prev.videos, newVideo]
        }));

        toast.success('Video subido correctamente');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Error al subir el video');
      }
    } catch (error) {
      console.error('Video upload error:', error);
      toast.error('Error al subir el video');
    } finally {
      setUploadingVideo(false);
      // Reset input
      e.target.value = '';
    }
  };

  const openModal = (post = null) => {
    if (post) {
      setEditingPost(post);
      let coords = { lat: '', lng: '' };
      if (post.coordinates) {
        if (Array.isArray(post.coordinates)) {
          coords = { lat: post.coordinates[1] || '', lng: post.coordinates[0] || '' };
        } else if (typeof post.coordinates === 'object') {
          coords = { lat: post.coordinates.lat || '', lng: post.coordinates.lng || '' };
        }
      }
      setForm({
        title: post.title, category: post.category, content: post.content,
        excerpt: post.excerpt, date: post.date, location: post.location,
        thumbnail: post.thumbnail, images: post.images || [], videos: post.videos || [],
        tags: post.tags || [],
        status: post.status || 'published',
        publish_date: post.publish_date || '',
        coordinates: coords
      });
    } else {
      setEditingPost(null);
      setForm({ 
        title: '', 
        category: categories.length > 0 ? categories[0].name : '', 
        content: '', 
        excerpt: '', 
        date: '', 
        location: '', 
        thumbnail: '', 
        images: [],
        videos: [],
        tags: [],
        status: 'published',
        publish_date: '',
        coordinates: { lat: '', lng: '' } 
      });
    }
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title) { toast.error('El título es obligatorio'); return; }
    if (!form.category) { toast.error('Selecciona una categoría'); return; }
    
    const submitData = { ...form };
    if (form.coordinates.lat && form.coordinates.lng) {
      submitData.coordinates = [
        parseFloat(form.coordinates.lng),
        parseFloat(form.coordinates.lat)
      ];
    } else {
      delete submitData.coordinates;
    }
    // Handle scheduling
    if (submitData.status === 'published') {
      submitData.publish_date = null;
    } else if (submitData.status === 'scheduled' && submitData.publish_date) {
      // Convert local datetime to ISO string
      submitData.publish_date = new Date(submitData.publish_date).toISOString();
    }
    
    try {
      const url = editingPost ? `${API}/admin/blog/${editingPost.post_id}` : `${API}/admin/blog`;
      const response = await adminFetch(url, {
        method: editingPost ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(submitData)
      });
      if (!response.ok) throw new Error('Error saving');
      toast.success(editingPost ? 'Post actualizado' : 'Post creado');
      setShowModal(false);
      fetchPosts();
    } catch (error) {
      toast.error('Error al guardar post');
    }
  };

  const handleDelete = async (postId) => {
    if (!window.confirm('¿Eliminar este post?')) return;
    try {
      await adminFetch(`${API}/admin/blog/${postId}`, { method: 'DELETE' });
      toast.success('Post eliminado');
      fetchPosts();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  // Category management
  const openCategoryModal = (category = null) => {
    setEditingCategory(category);
    setCategoryName(category ? category.name : '');
    setShowCategoryModal(true);
  };

  const handleCategorySubmit = async (e) => {
    e.preventDefault();
    if (!categoryName.trim()) {
      toast.error('El nombre es obligatorio');
      return;
    }

    try {
      const url = editingCategory 
        ? `${API}/admin/blog-categories/${editingCategory.category_id}`
        : `${API}/admin/blog-categories`;
      
      const response = await adminFetch(url, {
        method: editingCategory ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: categoryName.trim() })
      });

      if (response.ok) {
        toast.success(editingCategory ? 'Categoría actualizada' : 'Categoría creada');
        setShowCategoryModal(false);
        setCategoryName('');
        setEditingCategory(null);
        fetchCategories();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Error al guardar');
      }
    } catch (error) {
      toast.error('Error al guardar categoría');
    }
  };

  const handleDeleteCategory = async (category) => {
    if (!window.confirm(`¿Eliminar la categoría "${category.name}"?`)) return;
    
    try {
      const response = await adminFetch(`${API}/admin/blog-categories/${category.category_id}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        toast.success('Categoría eliminada');
        fetchCategories();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Error al eliminar');
      }
    } catch (error) {
      toast.error('Error al eliminar categoría');
    }
  };

  const getAverageRating = (post) => {
    if (!post.rating_count || post.rating_count === 0) return null;
    return (post.rating_sum / post.rating_count).toFixed(1);
  };

  const publishNow = async (post) => {
    try {
      const response = await adminFetch(`${API}/admin/blog/${post.post_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'published', publish_date: null })
      });
      if (!response.ok) throw new Error('Error');
      toast.success('Entrada publicada');
      fetchPosts();
    } catch (error) {
      toast.error('Error al publicar');
    }
  };

  const getDaysRemaining = (publishDate) => {
    if (!publishDate) return null;
    const now = new Date();
    const target = new Date(publishDate);
    const diff = Math.ceil((target - now) / (1000 * 60 * 60 * 24));
    return diff;
  };

  const getPostStatus = (post) => {
    if (!post.status || post.status === 'published') return 'published';
    if (post.status === 'scheduled') {
      if (post.publish_date && new Date(post.publish_date) <= new Date()) return 'published';
      return 'scheduled';
    }
    return post.status;
  };

  const filteredPosts = statusFilter === 'all' 
    ? posts 
    : posts.filter(p => getPostStatus(p) === statusFilter);

  return (
    <div className="p-6 lg:p-8">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-serif text-3xl mb-2">Blog</h1>
            <p className="text-muted-foreground">Gestiona las entradas del blog</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => openCategoryModal()} className="gap-2" data-testid="manage-categories-btn">
              <Tag className="w-4 h-4" /> Categorías
            </Button>
            <Button onClick={() => openModal()} className="gap-2" data-testid="new-post-btn">
              <Plus className="w-4 h-4" /> Nuevo Post
            </Button>
          </div>
        </div>

        {/* Categories Section */}
        {categories.length > 0 && (
          <div className="mb-6 p-4 bg-card border border-border">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-medium flex items-center gap-2">
                <Tag className="w-4 h-4" /> Categorías ({categories.length})
              </h3>
            </div>
            <div className="flex flex-wrap gap-2">
              {categories.map((cat) => (
                <div 
                  key={cat.category_id} 
                  className="group flex items-center gap-1 px-3 py-1.5 bg-muted text-sm"
                >
                  <span>{cat.name}</span>
                  <button 
                    onClick={() => openCategoryModal(cat)}
                    className="opacity-0 group-hover:opacity-100 p-0.5 hover:text-foreground transition-opacity"
                  >
                    <Pencil className="w-3 h-3" />
                  </button>
                  <button 
                    onClick={() => handleDeleteCategory(cat)}
                    className="opacity-0 group-hover:opacity-100 p-0.5 hover:text-destructive transition-opacity"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              ))}
              <button 
                onClick={() => openCategoryModal()}
                className="flex items-center gap-1 px-3 py-1.5 border border-dashed border-border text-sm text-muted-foreground hover:text-foreground hover:border-foreground transition-colors"
              >
                <Plus className="w-3 h-3" /> Añadir
              </button>
            </div>
          </div>
        )}

        {/* Empty categories state */}
        {categories.length === 0 && !loading && (
          <div className="mb-6 p-6 bg-card border border-border text-center">
            <Tag className="w-8 h-8 mx-auto mb-3 text-muted-foreground" />
            <p className="text-muted-foreground mb-3">No hay categorías definidas</p>
            <Button variant="outline" onClick={() => openCategoryModal()} className="gap-2">
              <Plus className="w-4 h-4" /> Crear primera categoría
            </Button>
          </div>
        )}

        {/* Status Filter */}
        <div className="flex gap-2 mb-4">
          <button
            onClick={() => setStatusFilter('all')}
            className={`px-3 py-1 text-xs border transition-colors ${statusFilter === 'all' ? 'border-foreground bg-foreground text-background' : 'border-border text-muted-foreground hover:border-foreground'}`}
            data-testid="filter-all"
          >
            Todas ({posts.length})
          </button>
          <button
            onClick={() => setStatusFilter('published')}
            className={`px-3 py-1 text-xs border transition-colors ${statusFilter === 'published' ? 'border-green-500 bg-green-500 text-white' : 'border-border text-green-600 hover:border-green-500'}`}
            data-testid="filter-published"
          >
            <span className="flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Publicadas ({posts.filter(p => getPostStatus(p) === 'published').length})</span>
          </button>
          <button
            onClick={() => setStatusFilter('scheduled')}
            className={`px-3 py-1 text-xs border transition-colors ${statusFilter === 'scheduled' ? 'border-yellow-500 bg-yellow-500 text-white' : 'border-border text-yellow-600 hover:border-yellow-500'}`}
            data-testid="filter-scheduled"
          >
            <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> Programadas ({posts.filter(p => getPostStatus(p) === 'scheduled').length})</span>
          </button>
        </div>

        {/* Posts List */}
        {loading ? (
          <div className="space-y-4">{[1,2,3].map(i => <div key={i} className="h-24 bg-muted animate-pulse" />)}</div>
        ) : (
          <div className="space-y-4">
            {filteredPosts.map((post) => {
              const postStatus = getPostStatus(post);
              const daysLeft = postStatus === 'scheduled' ? getDaysRemaining(post.publish_date) : null;
              return (
              <div key={post.post_id} className={`bg-card border p-4 flex gap-4 group ${postStatus === 'scheduled' ? 'border-yellow-400/50' : 'border-border'}`} data-testid={`post-${post.post_id}`}>
                {post.thumbnail && (
                  <img src={post.thumbnail} alt={post.title} className="w-24 h-24 object-cover flex-shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="text-xs text-muted-foreground">{post.category} • {post.date}</span>
                    {/* Status badge */}
                    {postStatus === 'published' && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400" data-testid={`status-${post.post_id}`}>
                        <CheckCircle className="w-3 h-3" /> Publicada
                      </span>
                    )}
                    {postStatus === 'scheduled' && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400" data-testid={`status-${post.post_id}`}>
                        <CalendarClock className="w-3 h-3" /> Programada
                        {daysLeft !== null && ` (${daysLeft > 0 ? `${daysLeft}d` : 'hoy'})`}
                      </span>
                    )}
                    {getAverageRating(post) && (
                      <span className="flex items-center gap-1 text-xs text-yellow-500">
                        <Star className="w-3 h-3 fill-current" />
                        {getAverageRating(post)} ({post.rating_count})
                      </span>
                    )}
                    {post.images && post.images.length > 0 && (
                      <span className="flex items-center gap-1 text-xs text-muted-foreground">
                        <Image className="w-3 h-3" />
                        {post.images.length} fotos
                      </span>
                    )}
                    {post.videos && post.videos.length > 0 && (
                      <span className="flex items-center gap-1 text-xs text-muted-foreground">
                        <Video className="w-3 h-3" />
                        {post.videos.length} video{post.videos.length > 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                  <h3 className="font-medium truncate">{post.title}</h3>
                  <p className="text-sm text-muted-foreground line-clamp-2">{post.excerpt}</p>
                  {postStatus === 'scheduled' && post.publish_date && (
                    <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-1 flex items-center gap-1">
                      <CalendarClock className="w-3 h-3" />
                      Publicación: {new Date(post.publish_date).toLocaleString('es-ES', { dateStyle: 'medium', timeStyle: 'short' })}
                    </p>
                  )}
                </div>
                <div className="flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  {postStatus === 'scheduled' && (
                    <Button size="sm" variant="ghost" onClick={() => publishNow(post)} title="Publicar ahora" data-testid={`publish-now-${post.post_id}`}>
                      <Send className="w-4 h-4 text-green-600" />
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => openModal(post)} data-testid={`edit-${post.post_id}`}>
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => handleDelete(post.post_id)} data-testid={`delete-${post.post_id}`}>
                    <Trash2 className="w-4 h-4 text-destructive" />
                  </Button>
                </div>
              </div>
              );
            })}
            {filteredPosts.length === 0 && (
              <div className="text-center py-12 text-muted-foreground">
                {statusFilter === 'all' ? 'No hay posts todavía. ¡Crea el primero!' : `No hay posts ${statusFilter === 'scheduled' ? 'programados' : 'publicados'}.`}
              </div>
            )}
          </div>
        )}
      </motion.div>

      {/* Post Modal */}
      {showModal && (
        <DraggableModal onClose={() => setShowModal(false)}>
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-background border border-border w-[48rem] max-w-[calc(100vw-2rem)] max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-border flex items-center justify-between sticky top-0 bg-background z-10">
              <h2 className="font-medium">{editingPost ? 'Editar Post' : 'Nuevo Post'}</h2>
              <button onClick={() => setShowModal(false)}><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-4 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-muted-foreground block mb-1">Título *</label>
                  <Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} data-testid="post-title" />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground block mb-1">Categoría *</label>
                  {categories.length > 0 ? (
                    <select 
                      value={form.category} 
                      onChange={e => setForm({...form, category: e.target.value})} 
                      className="w-full h-10 px-3 border border-input bg-background rounded-md" 
                      data-testid="post-category"
                    >
                      {categories.map(cat => (
                        <option key={cat.category_id} value={cat.name}>{cat.name}</option>
                      ))}
                    </select>
                  ) : (
                    <div className="text-sm text-muted-foreground p-2 border border-dashed border-border">
                      <button type="button" onClick={() => { setShowModal(false); openCategoryModal(); }} className="text-primary underline">
                        Crear una categoría primero
                      </button>
                    </div>
                  )}
                </div>
              </div>
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Extracto</label>
                <Textarea value={form.excerpt} onChange={e => setForm({...form, excerpt: e.target.value})} rows={2} data-testid="post-excerpt" />
              </div>
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Contenido</label>
                <Textarea value={form.content} onChange={e => setForm({...form, content: e.target.value})} rows={6} data-testid="post-content" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-muted-foreground block mb-1">Fecha</label>
                  <Input type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})} data-testid="post-date" />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground block mb-1">Ubicación</label>
                  <Input value={form.location} onChange={e => setForm({...form, location: e.target.value})} placeholder="Ej: Bilbao, País Vasco" data-testid="post-location" />
                </div>
              </div>

              {/* Scheduling */}
              <div className="border border-border p-3 space-y-3 bg-muted/30">
                <div className="flex items-center gap-3">
                  <label className="text-xs font-medium flex items-center gap-1">
                    <CalendarClock className="w-3.5 h-3.5" /> Publicación
                  </label>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setForm({...form, status: 'published', publish_date: ''})}
                      className={`px-3 py-1 text-xs border transition-colors ${form.status === 'published' ? 'border-green-500 bg-green-500 text-white' : 'border-border text-muted-foreground hover:border-green-500'}`}
                      data-testid="status-published-btn"
                    >
                      Publicar ahora
                    </button>
                    <button
                      type="button"
                      onClick={() => setForm({...form, status: 'scheduled'})}
                      className={`px-3 py-1 text-xs border transition-colors ${form.status === 'scheduled' ? 'border-yellow-500 bg-yellow-500 text-white' : 'border-border text-muted-foreground hover:border-yellow-500'}`}
                      data-testid="status-scheduled-btn"
                    >
                      Programar
                    </button>
                  </div>
                </div>
                {form.status === 'scheduled' && (
                  <div>
                    <label className="text-xs text-muted-foreground block mb-1">Fecha y hora de publicación</label>
                    <Input
                      type="datetime-local"
                      value={form.publish_date}
                      onChange={e => setForm({...form, publish_date: e.target.value})}
                      data-testid="publish-date-input"
                    />
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-muted-foreground block mb-1">Latitud <span className="text-muted-foreground/60">(para mapa)</span></label>
                  <Input 
                    type="number" 
                    step="any"
                    value={form.coordinates.lat} 
                    onChange={e => setForm({...form, coordinates: {...form.coordinates, lat: e.target.value}})} 
                    placeholder="Ej: 43.2630"
                    data-testid="post-lat"
                  />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground block mb-1">Longitud <span className="text-muted-foreground/60">(para mapa)</span></label>
                  <Input 
                    type="number" 
                    step="any"
                    value={form.coordinates.lng} 
                    onChange={e => setForm({...form, coordinates: {...form.coordinates, lng: e.target.value}})} 
                    placeholder="Ej: -2.9350"
                    data-testid="post-lng"
                  />
                </div>
              </div>
              <p className="text-xs text-muted-foreground -mt-2">
                Puedes obtener las coordenadas buscando la ubicación en Google Maps
              </p>

              {/* Tags */}
              <div>
                <label className="text-xs text-muted-foreground block mb-1">
                  Tags / Hashtags <span className="text-muted-foreground/60">(separados por coma o Enter)</span>
                </label>
                <div className="flex flex-wrap gap-1 mb-2">
                  {form.tags.map((tag, i) => (
                    <span key={i} className="inline-flex items-center gap-1 px-2 py-1 bg-muted text-xs">
                      #{tag}
                      <button type="button" onClick={() => setForm({...form, tags: form.tags.filter((_, idx) => idx !== i)})} className="hover:text-destructive">
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
                <Input
                  placeholder="Escribe un tag y pulsa Enter o coma"
                  data-testid="post-tags"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ',') {
                      e.preventDefault();
                      const val = e.target.value.trim().replace(/,/g, '');
                      if (val && !form.tags.includes(val)) {
                        setForm({...form, tags: [...form.tags, val]});
                      }
                      e.target.value = '';
                    }
                  }}
                  onBlur={(e) => {
                    const val = e.target.value.trim().replace(/,/g, '');
                    if (val && !form.tags.includes(val)) {
                      setForm({...form, tags: [...form.tags, val]});
                    }
                    e.target.value = '';
                  }}
                />
              </div>
              
              {/* Imagen Destacada */}
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Imagen destacada</label>
                <div className="flex gap-4 items-start">
                  {form.thumbnail ? (
                    <div className="relative w-40 h-24">
                      <img src={form.thumbnail} alt="Thumbnail" className="w-full h-full object-cover" />
                      <button type="button" onClick={() => setForm({...form, thumbnail: ''})} className="absolute -top-2 -right-2 w-5 h-5 bg-destructive text-white rounded-full flex items-center justify-center">
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ) : (
                    <label className="flex items-center justify-center w-40 h-24 border-2 border-dashed border-border cursor-pointer hover:border-foreground/50 transition-colors">
                      <input type="file" accept="image/*" className="hidden" onChange={handleUpload} data-testid="thumbnail-upload" />
                      {uploading ? <div className="w-5 h-5 border-2 border-foreground border-t-transparent rounded-full animate-spin" /> : <Upload className="w-5 h-5 text-muted-foreground" />}
                    </label>
                  )}
                </div>
              </div>

              {/* Galería de Imágenes */}
              <div>
                <label className="text-xs text-muted-foreground block mb-1">
                  Galería de imágenes
                  <span className="text-muted-foreground/60 normal-case ml-2">(puedes seleccionar varias a la vez)</span>
                </label>
                
                {/* Upload progress */}
                {uploadingGallery && uploadProgress.total > 0 && (
                  <div className="mb-4 p-4 bg-muted/50 border border-border rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium">
                        Subiendo {uploadProgress.current} de {uploadProgress.total}
                      </span>
                      <span className="text-sm text-muted-foreground">
                        {Math.round((uploadProgress.current / uploadProgress.total) * 100)}%
                      </span>
                    </div>
                    <div className="h-2 bg-muted rounded-full overflow-hidden">
                      <motion.div 
                        className="h-full bg-foreground"
                        initial={{ width: 0 }}
                        animate={{ width: `${(uploadProgress.current / uploadProgress.total) * 100}%` }}
                        transition={{ duration: 0.3 }}
                      />
                    </div>
                    {uploadProgress.completed.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {uploadProgress.completed.slice(-3).map((name, i) => (
                          <span key={i} className="inline-flex items-center gap-1 text-xs text-green-500">
                            <CheckCircle className="w-3 h-3" /> {name.length > 15 ? name.slice(0, 15) + '...' : name}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                
                <div className="flex flex-wrap gap-2 mb-3">
                  {form.images.map((img, index) => (
                    <div key={index} className="relative w-20 h-20 group">
                      <img src={img} alt={`Gallery ${index + 1}`} className="w-full h-full object-cover" />
                      <button 
                        type="button" 
                        onClick={() => removeGalleryImage(index)} 
                        className="absolute -top-1 -right-1 w-5 h-5 bg-destructive text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                  
                  <label className={`flex flex-col items-center justify-center w-20 h-20 border-2 border-dashed border-border cursor-pointer hover:border-foreground/50 transition-colors ${uploadingGallery ? 'opacity-50 pointer-events-none' : ''}`}>
                    <input 
                      type="file" 
                      accept="image/*" 
                      multiple 
                      className="hidden" 
                      onChange={handleGalleryUpload}
                      disabled={uploadingGallery}
                      data-testid="gallery-upload"
                    />
                    {uploadingGallery && uploadProgress.total > 0 ? (
                      <div className="w-5 h-5 border-2 border-foreground border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <>
                        <Plus className="w-5 h-5 text-muted-foreground" />
                        <span className="text-[10px] text-muted-foreground mt-1">Añadir</span>
                      </>
                    )}
                  </label>
                </div>
                
                {form.images.length > 0 && (
                  <p className="text-xs text-muted-foreground">
                    {form.images.length} imagen(es) en la galería
                  </p>
                )}
              </div>

              {/* Videos Section */}
              <div>
                <label className="text-xs text-muted-foreground block mb-2">
                  Videos incrustados
                  <span className="text-muted-foreground/60 normal-case ml-2">(subir directamente o pegar URL)</span>
                </label>
                
                {/* Video list */}
                {form.videos.length > 0 && (
                  <div className="space-y-2 mb-4">
                    {form.videos.map((video) => (
                      <div key={video.video_id} className="flex items-center gap-3 p-2 bg-muted/50 border border-border group">
                        {/* Thumbnail */}
                        <div className="w-24 h-14 bg-black flex-shrink-0 relative overflow-hidden">
                          {getVideoThumbnail(video) ? (
                            <img src={getVideoThumbnail(video)} alt={video.title} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center bg-muted">
                              <Video className="w-6 h-6 text-muted-foreground" />
                            </div>
                          )}
                          <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                            <Play className="w-6 h-6 text-white" fill="white" />
                          </div>
                        </div>
                        
                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{video.title}</p>
                          <p className="text-xs text-muted-foreground truncate">{video.url}</p>
                          <span className="text-[10px] uppercase tracking-wide text-muted-foreground bg-muted px-1.5 py-0.5">
                            {video.video_type}
                          </span>
                        </div>
                        
                        {/* Delete */}
                        <button 
                          type="button"
                          onClick={() => removeVideo(video.video_id)}
                          className="p-1.5 text-muted-foreground hover:text-destructive transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Upload video directly */}
                <div className="mb-4">
                  <label className={`flex items-center justify-center gap-3 p-4 border-2 border-dashed border-border cursor-pointer hover:border-foreground/50 transition-colors ${uploadingVideo ? 'opacity-50 pointer-events-none' : ''}`}>
                    <input 
                      type="file" 
                      accept="video/mp4,video/mov,video/avi,video/webm,video/quicktime,.mp4,.mov,.avi,.webm,.mkv,.m4v" 
                      className="hidden" 
                      onChange={handleVideoUpload}
                      disabled={uploadingVideo}
                      data-testid="video-upload"
                    />
                    {uploadingVideo ? (
                      <>
                        <div className="w-5 h-5 border-2 border-foreground border-t-transparent rounded-full animate-spin" />
                        <span className="text-sm">Subiendo video...</span>
                      </>
                    ) : (
                      <>
                        <Upload className="w-5 h-5 text-muted-foreground" />
                        <span className="text-sm text-muted-foreground">Subir video desde tu ordenador</span>
                        <span className="text-xs text-muted-foreground/60">(máx. 100MB)</span>
                      </>
                    )}
                  </label>
                </div>

                {/* Or add via URL */}
                <div className="relative mb-3">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-border"></div>
                  </div>
                  <div className="relative flex justify-center text-xs">
                    <span className="bg-background px-2 text-muted-foreground">o añadir por URL</span>
                  </div>
                </div>

                {/* Add new video via URL */}
                <div className="flex gap-2">
                  <div className="flex-1 space-y-2">
                    <Input 
                      value={newVideoUrl}
                      onChange={e => setNewVideoUrl(e.target.value)}
                      placeholder="URL del video (YouTube o Cloudinary)"
                      data-testid="video-url"
                    />
                    <Input 
                      value={newVideoTitle}
                      onChange={e => setNewVideoTitle(e.target.value)}
                      placeholder="Título del video (opcional)"
                      data-testid="video-title"
                    />
                  </div>
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={addVideo}
                    className="self-start"
                    data-testid="add-video-btn"
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                
                <p className="text-xs text-muted-foreground mt-2">
                  Formatos soportados: MP4, MOV, AVI, WEBM. Para YouTube pega la URL completa.
                </p>
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t border-border">
                <Button type="button" variant="outline" onClick={() => setShowModal(false)}>Cancelar</Button>
                <Button type="submit" data-testid="save-post-btn">Guardar</Button>
              </div>
            </form>
          </motion.div>
        </DraggableModal>
      )}

      {/* Category Modal */}
      {showCategoryModal && (
        <DraggableModal onClose={() => { setShowCategoryModal(false); setEditingCategory(null); setCategoryName(''); }}>
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-background border border-border w-[28rem] max-w-[calc(100vw-2rem)]">
            <div className="p-4 border-b border-border flex items-center justify-between">
              <h2 className="font-medium">{editingCategory ? 'Editar Categoría' : 'Nueva Categoría'}</h2>
              <button onClick={() => { setShowCategoryModal(false); setEditingCategory(null); setCategoryName(''); }}>
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleCategorySubmit} className="p-4 space-y-4">
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Nombre de la categoría *</label>
                <Input 
                  value={categoryName} 
                  onChange={e => setCategoryName(e.target.value)} 
                  placeholder="Ej: Consejos, Tutoriales, Noticias..."
                  data-testid="category-name"
                  autoFocus
                />
              </div>
              <div className="flex justify-end gap-2 pt-4 border-t border-border">
                <Button type="button" variant="outline" onClick={() => { setShowCategoryModal(false); setEditingCategory(null); setCategoryName(''); }}>
                  Cancelar
                </Button>
                <Button type="submit" data-testid="save-category-btn">
                  {editingCategory ? 'Actualizar' : 'Crear'}
                </Button>
              </div>
            </form>
          </motion.div>
        </DraggableModal>
      )}
    </div>
  );
};

export default AdminBlog;
